import React, { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import { get } from 'lodash';
import { useTranslation } from 'react-i18next';
import { Link, Redirect } from 'react-router-dom';

import { useActions, useWorkspace } from '@mssgme/hooks';
import {
    CancelButton,
    EnhancedFormik,
    EnhancedNavigationGuard,
    RestrictionScope,
    Form,
    NotifyAndClose,
    renderNode,
    SaveButton,
    ScrollableBox,
    SidebarHeader,
} from '@mssgme/ui';
import { PageLayout, usePreview } from '../../../UI';

// TODO: show popup on profile.url field blur
export default function CardEditForm({ children, title, backPath, successRedirectPath }) {
    const { api } = useActions();
    const { t } = useTranslation('cards');
    const { card } = useWorkspace();
    const { updatePreview } = usePreview();

    const initialValues = useMemo(
        () => ({
            profile: {
                fullname: get(card, 'profile.fullname', ''),
                website: {
                    enabled: get(card, 'profile.website.enabled', false),
                    title: get(card, 'profile.website.title', ''),
                    url: get(card, 'profile.website.url', ''),
                },
                description: get(card, 'profile.description', ''),
                category: get(card, 'profile.category', ''),
                url: get(card, 'profile.url', ''),
            },
            design: {
                photo: get(card, 'design.photo'),
                photoUrl: get(card, 'design.photoUrl'),
                background: {
                    type: get(card, 'design.background.type', 'color'),
                    color: get(card, 'design.background.color', '#2F3542'),
                    image: get(card, 'design.background.image'),
                    imageUrl: get(card, 'design.background.imageUrl'),
                },
                branding: get(card, 'design.branding', false),
            },
            analytics: {
                facebook: get(card, 'analytics.facebook', ''),
                google: get(card, 'analytics.google', ''),
                yandex: get(card, 'analytics.yandex', ''),
            },
            seo: {
                title: get(card, 'seo.title', ''),
                description: get(card, 'seo.description', ''),
            },
        }),
        [card]
    );
    const handleSubmit = useCallback((values) => api.card.update({ payload: { ...card, ...values } }), [card]);

    const leftButton = <CancelButton tag={Link} to={backPath} styled="backIcon" />;

    return (
        <EnhancedFormik initialValues={initialValues} onSubmit={handleSubmit} onChange={updatePreview}>
            {(formikProps) => (
                <EnhancedNavigationGuard>
                    {formikProps.status === 'success' && (
                        <>
                            <NotifyAndClose>{t('CardEditor.saved')}</NotifyAndClose>
                            <Redirect to={successRedirectPath} />
                        </>
                    )}
                    <PageLayout>
                        <SidebarHeader heading={title} leftButton={leftButton} rightButton={<SaveButton />} />
                        <ScrollableBox>
                            <RestrictionScope modal={false}>
                                <Form>{renderNode(children, formikProps)}</Form>
                            </RestrictionScope>
                        </ScrollableBox>
                    </PageLayout>
                </EnhancedNavigationGuard>
            )}
        </EnhancedFormik>
    );
}

CardEditForm.propTypes = {
    children: PropTypes.any.isRequired,
    title: PropTypes.string.isRequired,
    backPath: PropTypes.string,
    successRedirectPath: PropTypes.string,
};

CardEditForm.defaultProps = {
    backPath: '/p/edit',
    successRedirectPath: '/p/edit',
};
